/*
 * Licensed under the Apache License, Version 2.0 (the "License"); you may not use
 * this file except in compliance with the License. You may obtain a copy of the
 * License at
 *
 *  https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed
 * under the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR
 * CONDITIONS OF ANY KIND, either express or implied. See the License for the
 * specific language governing permissions and limitations under the License.
 */

import i18n from "i18next";
import type { InitOptions } from "i18next";
import { initReactI18next } from "react-i18next";

import { Elang } from "../locales";
import SessionStorage from "../services/SessionStorage";

import localeEn from "./en";
import localeEs from "./es";
import localeFr from "./fr";
import localePt from "./pt";
import localeRu from "./ru";

interface ITranslationService {
  init(): void;
  getDefaultLanguage(): string;
}

/**
 * Reads a supported language from the `?lang=` query param so shared links can
 * open the site in a specific language. Returns undefined for missing/unsupported
 * values so the caller can fall back to sessionStorage/default.
 */
export function getLanguageFromQuery(): string | undefined {
  if (typeof window === "undefined") {
    return undefined;
  }
  const lang = new URLSearchParams(window.location.search).get("lang");
  return lang && Object.values(Elang).includes(lang as Elang)
    ? lang
    : undefined;
}

i18n.use(initReactI18next);

class TranslationService implements ITranslationService {
  constructor() {}

  getDefaultLanguage() {
    const { fallbackLng: defaultLanguage } = i18n.options;

    return <string>defaultLanguage || Elang.EN;
  }

  initOptions(preferredLanguageCode: string) {
    return {
      resources: {
        [Elang.EN]: localeEn,
        [Elang.ES]: localeEs,
        [Elang.FR]: localeFr,
        [Elang.PT]: localePt,
        [Elang.RU]: localeRu,
      },
      lng: preferredLanguageCode,
      fallbackLng: Elang.EN,
      fallbackNS: "translation",
      interpolation: {
        escapeValue: false,
      },
      keySeparator: false,
      // Translation keys are full English sentences that may contain ":", so
      // "namespace:key" lookups don't work; pick the namespace via useTranslation(ns)
      nsSeparator: false,
      react: {
        useSuspense: true,
      },
      // debug emits a harmless message onto the console when a translation is
      // not found.
      // debug: true,
    } as InitOptions;
  }

  async init() {
    const preferredLanguageCode =
      getLanguageFromQuery() || SessionStorage.get("lang") || Elang.EN;

    // Persist immediately so the Accept-Language request interceptor (which reads
    // sessionStorage) sends the resolved language on the very first request — the
    // languageChanged listener below doesn't fire for the initial i18n.init().
    SessionStorage.add("lang", preferredLanguageCode);

    const i18nOptions = this.initOptions(preferredLanguageCode);

    await i18n.init(i18nOptions);

    i18n.on("languageChanged", (lng) => {
      SessionStorage.add("lang", lng);
    });
  }
}

export default new TranslationService();
